import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { WordMark } from '../../components/WordMark';
import { EDGES } from '../../theme/tokens';
import {
  getLadderTop,
  getMyRank,
  getCachedPlayerId,
  type LadderEntry,
  type MyRank,
} from '../../net/asyncClient';
import { listBlocked } from '../../net/friendsClient';
import { PlayerActionsSheet, type PlayerRef } from '../../components/PlayerActions';

// Top-50 by Elo rating. New players (< 5 games) don't appear yet —
// keeps the board from being dominated by fresh-1000 noise. Caller's
// own row is highlighted; their actual rank lives in MyRank.

export function LadderPage() {
  const [entries, setEntries] = useState<LadderEntry[] | null>(null);
  const [me, setMe] = useState<MyRank | null>(null);
  const [loading, setLoading] = useState(true);
  // Blocked players stay on the public ladder (it is world-readable and
  // ranks are real) but their handle is hidden from the player who
  // blocked them.
  const [blocked, setBlocked] = useState<Set<string>>(new Set());
  const [flagged, setFlagged] = useState<PlayerRef | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const myPlayerId = getCachedPlayerId();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [top, mine, blk] = await Promise.all([getLadderTop(50), getMyRank(), listBlocked()]);
      if (cancelled) return;
      setEntries(top);
      setMe(mine);
      if (blk) setBlocked(new Set(blk.map((b) => b.id)));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="pt-safe relative min-h-dvh w-full overflow-x-hidden bg-asphalt">
      <section className="relative mx-auto flex h-full w-full max-w-3xl flex-col gap-md px-md py-md md:gap-lg md:px-lg md:py-lg lg:gap-xl lg:py-xl">
        <header className="flex items-center justify-between">
          <WordMark size="sm" />
          <Link
            to="/menu"
            className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← menu
          </Link>
        </header>

        <div className="flex items-baseline gap-sm">
          <h1 className="font-cholo text-[24px] leading-none text-sodium sm:text-[32px] md:text-[42px] lg:text-[54px]">
            Ladder
          </h1>
          <span className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
            top 50 · async pvp
          </span>
        </div>

        {/* "You" card — always visible even if not in top 50 */}
        <MyRankCard me={me} loading={loading} />

        {/* Leaderboard table */}
        <div className="overflow-hidden rounded-md border" style={{ borderColor: EDGES.soft }}>
          <div className="grid grid-cols-[40px_1fr_60px_60px] gap-sm bg-stucco2 px-sm py-xs font-data text-[12px] uppercase tracking-[0.2em] text-marker md:grid-cols-[60px_1fr_80px_80px] md:px-md md:text-[12px]">
            <span>rank</span>
            <span>handle</span>
            <span className="text-right">rating</span>
            <span className="text-right">w / l</span>
          </div>

          {loading ? (
            <Row dim>Loading…</Row>
          ) : entries === null ? (
            <Row dim>Couldn't reach the ladder. Try again in a moment.</Row>
          ) : entries.length === 0 ? (
            <Row dim>No qualified players yet (5+ games needed).</Row>
          ) : (
            entries.map((entry, i) => (
              <LadderRow
                key={entry.playerId}
                entry={entry}
                isMe={entry.playerId === myPlayerId}
                isBlocked={blocked.has(entry.playerId)}
                index={i}
                onFlag={entry.playerId === myPlayerId ? undefined : () => setFlagged({ id: entry.playerId, handle: entry.handle })}
              />
            ))
          )}
        </div>
        {notice && (
          <p className="font-data text-[12px] uppercase tracking-[0.2em] text-sodium" role="status">{notice}</p>
        )}
      </section>
      {flagged && (
        <PlayerActionsSheet
          player={flagged}
          onClose={() => setFlagged(null)}
          onDone={(what, ok) => {
            if (!ok) { setNotice('Could not reach the server. Try again.'); return; }
            if (what === 'blocked') setBlocked((prev) => new Set(prev).add(flagged.id));
            setNotice(what === 'blocked' ? 'Player blocked. Undo in Settings.' : 'Report sent. Thank you.');
            window.setTimeout(() => setNotice(null), 3200);
          }}
        />
      )}
    </main>
  );
}

function Row({ children, dim }: { children: React.ReactNode; dim?: boolean }) {
  return (
    <div
      className={`px-sm py-md text-center font-data text-[12px] uppercase tracking-[0.2em] md:px-md md:py-lg md:text-[14px] ${
        dim ? 'text-graphite' : 'text-bone'
      }`}
    >
      {children}
    </div>
  );
}

function LadderRow({
  entry,
  isMe,
  isBlocked,
  index,
  onFlag,
}: {
  entry: LadderEntry;
  isMe: boolean;
  isBlocked: boolean;
  index: number;
  onFlag?: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(0.02 * index, 0.4), duration: 0.2 }}
      className={`grid grid-cols-[40px_1fr_60px_60px_32px] items-center gap-sm border-t px-sm py-sm md:grid-cols-[60px_1fr_80px_80px_40px] md:px-md md:py-md ${
        isMe ? 'bg-sodium/15' : 'bg-stucco1'
      }`}
      style={{ borderColor: EDGES.soft }}
    >
      <span
        className={`font-data text-[14px] md:text-[16px] ${
          entry.rank <= 3 ? 'text-sodium' : 'text-bone'
        }`}
      >
        #{entry.rank}
      </span>
      <span className={`truncate font-data text-[13px] md:text-[15px] ${isMe ? 'text-sodium' : isBlocked ? 'text-graphite' : 'text-bone'}`}>
        {isBlocked ? 'blocked player' : entry.handle}
        {isMe && (
          <span className="ml-xs rounded-sm bg-sodium/30 px-xs py-[1px] text-[13px] uppercase tracking-[0.25em] text-sodium md:text-[10px]">
            you
          </span>
        )}
      </span>
      <span className="text-right font-data text-[14px] tabular-nums text-bone md:text-[16px]">
        {entry.rating}
      </span>
      <span className="text-right font-data text-[13px] tabular-nums text-marker md:text-[13px]">
        {entry.wins}/{entry.losses}
      </span>
      {onFlag ? (
        <button
          onClick={onFlag}
          title="Report or block"
          aria-label={`Report or block ${entry.handle}`}
          className="min-h-[32px] rounded-sm font-data text-[13px] text-graphite hover:text-bone"
        >
          ⚑
        </button>
      ) : (
        <span />
      )}
    </motion.div>
  );
}

function MyRankCard({ me, loading }: { me: MyRank | null; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center justify-between rounded-md border bg-stucco1 px-md py-sm" style={{ borderColor: EDGES.soft }}>
        <span className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite">loading your record…</span>
      </div>
    );
  }
  if (!me) {
    return (
      <div className="flex items-center justify-between rounded-md border bg-stucco1 px-md py-sm" style={{ borderColor: EDGES.soft }}>
        <span className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite">offline — can't load your rank</span>
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="flex items-center justify-between rounded-md border bg-stucco1 px-md py-md"
      style={{ borderColor: EDGES.soft }}
    >
      <div className="flex flex-col">
        <span className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
          your rank
        </span>
        <span className="font-cholo text-[28px] leading-none text-sodium md:text-[36px]">
          {me.rank !== null ? `#${me.rank}` : '—'}
        </span>
      </div>
      <div className="flex flex-col text-right">
        <span className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
          elo · games
        </span>
        <span className="font-data text-[20px] tabular-nums text-bone md:text-[24px]">
          {me.rating} · {me.gamesPlayed}
        </span>
        <span className="font-data text-[12px] tabular-nums text-graphite md:text-[12px]">
          {me.wins}w · {me.losses}l · {me.ties}t
        </span>
      </div>
    </motion.div>
  );
}
